/**
 * FinanceCounterpartiesPage — Issue #369.
 *
 * 对方单位 / 收款账户主数据维护页：
 *   - 列表默认脱敏（taxIdMask / contactPhoneMask / bankAccountMask）。
 *   - 仅在拥有 finance.counterparty:reveal / finance.payee_account:reveal 时
 *     才显示 “查看明文” 按钮；点击后弹出输入原因 + 业务上下文，调用 reveal
 *     接口，并将结果一次性显示。
 *   - 仅在拥有 finance.counterparty:write 时显示新建按钮（最小实现走简单表单）。
 *
 * 下游 #370 付款申请才会消费 snapshot 接口；本页只展示主数据。
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  canRevealCounterpartyField,
  canRevealPayeeAccountField,
  canWriteCounterparty,
  canManageCounterparty,
  canWritePayeeAccount,
  canVerifyPayeeAccount,
  canManagePayeeAccount,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useFinanceCounterparties,
  useFinanceCounterpartyActions,
  useFinancePayeeAccounts,
  useFinancePayeeAccountActions,
  type FinanceCounterpartySummaryDto,
  type FinancePayeeAccountSummaryDto,
  type RevealResponseDto,
} from '@/hooks/useFinanceCounterparties';

const COUNTERPARTY_TYPE_LABELS: Record<string, string> = {
  supplier: '供应商',
  service_provider: '外部服务商',
  employee: '员工',
  individual: '个人',
  other: '其他',
};

const STATUS_LABELS: Record<string, string> = {
  active: '正常',
  suspended: '已暂停',
  archived: '已归档',
  pending_verification: '待校验',
  rejected: '已拒绝',
};

const VERIFICATION_LABELS: Record<string, string> = {
  pending: '待校验',
  verified: '已校验',
  rejected: '已拒绝',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  suspended: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-200 text-gray-500',
  pending_verification: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

interface RevealDialogState {
  scope: 'counterparty' | 'payee_account';
  id: string;
  field: 'tax_id' | 'contact_phone' | 'bank_account';
  mask: string;
}

function RevealDialog({
  state,
  onClose,
  onSubmit,
  result,
  loading,
  error,
}: {
  state: RevealDialogState;
  onClose: () => void;
  onSubmit: (reason: string, businessContext: string) => void;
  result: RevealResponseDto | null;
  loading: boolean;
  error: { message: string } | null;
}) {
  const [reason, setReason] = useState('');
  const [businessContext, setBusinessContext] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 py-10">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold">查看明文</h2>
          <button
            className="rounded border border-gray-300 px-3 py-1 text-sm"
            onClick={onClose}
          >
            关闭
          </button>
        </div>
        <div className="space-y-3 px-6 py-5 text-sm">
          <div className="rounded bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
            操作将被记录到审计日志。请仅在确有付款 / 报销等业务需要时执行，
            并避免在屏幕共享或截屏中暴露明文。
          </div>
          <div>
            <span className="text-gray-500">原脱敏：</span>
            <span className="font-mono">{state.mask}</span>
          </div>
          <label className="block">
            <span className="text-gray-600">原因（至少 4 个字符）</span>
            <input
              type="text"
              maxLength={255}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
              data-testid="reveal-reason"
            />
          </label>
          <label className="block">
            <span className="text-gray-600">业务上下文（付款申请号 / 报销单号）</span>
            <input
              type="text"
              maxLength={255}
              value={businessContext}
              onChange={(e) => setBusinessContext(e.target.value)}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
              data-testid="reveal-context"
            />
          </label>
          {error && (
            <div className="rounded bg-red-50 px-3 py-2 text-xs text-red-700">
              {error.message}
            </div>
          )}
          {result && (
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              <div>
                <span className="text-emerald-700">明文：</span>
                <span className="font-mono" data-testid="reveal-plaintext">
                  {result.plaintext}
                </span>
              </div>
              <div className="mt-1 text-emerald-700">
                审计 ID：{result.auditId}
              </div>
              <div className="mt-1 text-emerald-700">
                请在确认后立即关闭此对话框，避免落屏。
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              className="rounded border border-gray-300 px-3 py-1"
              onClick={onClose}
            >
              取消
            </button>
            <button
              disabled={loading || result !== null}
              className="rounded border border-emerald-300 bg-emerald-50 px-3 py-1 text-emerald-700"
              onClick={() => onSubmit(reason, businessContext)}
              data-testid="reveal-submit"
            >
              {loading ? '解密中…' : result ? '已显示' : '确认获取明文'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PayeeAccountsTable({
  counterparty,
  granted,
}: {
  counterparty: FinanceCounterpartySummaryDto;
  granted: readonly string[];
}) {
  const { data, loading, error, fetchList } = useFinancePayeeAccounts();
  const {
    loading: actionLoading,
    error: actionError,
    verify,
    setDefault,
    reveal,
  } = useFinancePayeeAccountActions();
  const [revealState, setRevealState] = useState<RevealDialogState | null>(null);
  const [revealResult, setRevealResult] = useState<RevealResponseDto | null>(null);

  useEffect(() => {
    void fetchList({ counterpartyId: counterparty.id });
  }, [counterparty.id, fetchList]);

  const canReveal = canRevealPayeeAccountField(granted);
  const canVerify = canVerifyPayeeAccount(granted);
  const canManage = canManagePayeeAccount(granted);

  async function refresh() {
    await fetchList({ counterpartyId: counterparty.id });
  }

  function openReveal(account: FinancePayeeAccountSummaryDto) {
    setRevealState({
      scope: 'payee_account',
      id: account.id,
      field: 'bank_account',
      mask: account.bankAccountMask,
    });
    setRevealResult(null);
  }

  async function submitReveal(reason: string, businessContext: string) {
    if (!revealState) return;
    const r = await reveal(revealState.id, {
      field: 'bank_account',
      reason,
      businessContext,
    });
    if (r) setRevealResult(r);
  }

  async function onVerify(
    account: FinancePayeeAccountSummaryDto,
    decision: 'verified' | 'rejected',
  ) {
    const reason =
      decision === 'rejected'
        ? window.prompt('请输入校验拒绝原因：') ?? ''
        : undefined;
    if (decision === 'rejected' && !reason) return;
    const ok = await verify(account.id, {
      expectedRecordVersion: account.recordVersion,
      decision,
      reason,
    });
    if (ok) await refresh();
  }

  async function onSetDefault(account: FinancePayeeAccountSummaryDto) {
    const ok = await setDefault(account.id, {
      expectedRecordVersion: account.recordVersion,
    });
    if (ok) await refresh();
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">收款账户</h3>
        <button
          className="rounded border border-gray-300 px-2 py-0.5 text-xs"
          onClick={() => void refresh()}
        >
          刷新
        </button>
      </div>
      {(loading || actionLoading) && (
        <div className="text-xs text-gray-500">加载中…</div>
      )}
      {(error || actionError) && (
        <div className="rounded bg-red-50 px-3 py-2 text-xs text-red-700">
          {(error ?? actionError)?.message}
        </div>
      )}
      {data && data.rows.length === 0 && (
        <div className="rounded border border-dashed border-gray-300 px-3 py-4 text-center text-xs text-gray-500">
          暂无收款账户。
        </div>
      )}
      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-2 py-1 text-left">账户持有人</th>
                <th className="px-2 py-1 text-left">开户行</th>
                <th className="px-2 py-1 text-left">银行账号（脱敏）</th>
                <th className="px-2 py-1 text-left">币种</th>
                <th className="px-2 py-1 text-left">类型</th>
                <th className="px-2 py-1 text-left">校验</th>
                <th className="px-2 py-1 text-left">状态</th>
                <th className="px-2 py-1 text-left">默认</th>
                <th className="px-2 py-1 text-left">操作</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((account) => (
                <tr key={account.id} className="border-t border-gray-100">
                  <td className="px-2 py-1">{account.accountHolderName}</td>
                  <td className="px-2 py-1">
                    {account.bankName}
                    {account.bankBranch ? ` · ${account.bankBranch}` : ''}
                  </td>
                  <td
                    className="px-2 py-1 font-mono"
                    data-testid={`bank-account-mask-${account.id}`}
                  >
                    {account.bankAccountMask}
                  </td>
                  <td className="px-2 py-1">{account.currency}</td>
                  <td className="px-2 py-1">
                    {account.accountKind === 'corporate' ? '对公' : '对私'}
                  </td>
                  <td className="px-2 py-1">
                    {VERIFICATION_LABELS[account.verificationStatus] ??
                      account.verificationStatus}
                  </td>
                  <td className="px-2 py-1">
                    <StatusBadge status={account.status} />
                  </td>
                  <td className="px-2 py-1">
                    {account.isDefault ? '✓' : ''}
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex flex-wrap gap-1">
                      {canReveal && (
                        <button
                          className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700"
                          onClick={() => openReveal(account)}
                          data-testid={`reveal-bank-${account.id}`}
                        >
                          查看明文
                        </button>
                      )}
                      {canVerify &&
                        (account.verificationStatus === 'pending' ||
                          account.verificationStatus === 'rejected') && (
                          <>
                            <button
                              className="rounded border border-green-200 bg-green-50 px-2 py-0.5 text-green-700"
                              onClick={() => void onVerify(account, 'verified')}
                            >
                              校验通过
                            </button>
                            <button
                              className="rounded border border-red-200 bg-red-50 px-2 py-0.5 text-red-700"
                              onClick={() => void onVerify(account, 'rejected')}
                            >
                              校验拒绝
                            </button>
                          </>
                        )}
                      {canManage &&
                        account.status === 'active' &&
                        account.verificationStatus === 'verified' &&
                        !account.isDefault && (
                          <button
                            className="rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-700"
                            onClick={() => void onSetDefault(account)}
                          >
                            设默认
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {revealState && (
        <RevealDialog
          state={revealState}
          onClose={() => {
            setRevealState(null);
            setRevealResult(null);
          }}
          onSubmit={(r, c) => void submitReveal(r, c)}
          result={revealResult}
          loading={actionLoading}
          error={actionError}
        />
      )}
    </div>
  );
}

export default function FinanceCounterpartiesPage() {
  const { permissions } = useAuth();
  const granted = useMemo(() => permissions ?? [], [permissions]);
  const canRead = hasPermission(granted, FINANCE_PERMISSIONS.COUNTERPARTY_READ);
  const canWrite = canWriteCounterparty(granted);
  const canManage = canManageCounterparty(granted);
  const canReveal = canRevealCounterpartyField(granted);
  const canWritePayee = canWritePayeeAccount(granted);

  const { data, loading, error, fetchList } = useFinanceCounterparties();
  const {
    loading: actionLoading,
    error: actionError,
    suspend,
    reactivate,
    archive,
    reveal,
  } = useFinanceCounterpartyActions();
  const { error: payeeCreateError } = useFinancePayeeAccountActions();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [revealState, setRevealState] = useState<RevealDialogState | null>(null);
  const [revealResult, setRevealResult] = useState<RevealResponseDto | null>(null);

  useEffect(() => {
    if (canRead) {
      void fetchList();
    }
  }, [canRead, fetchList]);

  if (!canRead) {
    return (
      <div className="p-4 text-sm text-gray-600">
        没有权限查看财务对方单位。请联系管理员申请 finance.counterparty:read。
      </div>
    );
  }

  async function refresh() {
    await fetchList();
  }

  function openReveal(
    cp: FinanceCounterpartySummaryDto,
    field: 'tax_id' | 'contact_phone',
    mask: string | null | undefined,
  ) {
    if (!mask) return;
    setRevealState({ scope: 'counterparty', id: cp.id, field, mask });
    setRevealResult(null);
  }

  async function submitReveal(reason: string, businessContext: string) {
    if (!revealState) return;
    const r = await reveal(revealState.id, {
      field: revealState.field,
      reason,
      businessContext,
    });
    if (r) setRevealResult(r);
  }

  async function onSuspend(cp: FinanceCounterpartySummaryDto) {
    const reason = window.prompt('请输入暂停原因：');
    if (!reason) return;
    const ok = await suspend(cp.id, {
      expectedRecordVersion: cp.recordVersion,
      reason,
    });
    if (ok) await refresh();
  }

  async function onReactivate(cp: FinanceCounterpartySummaryDto) {
    const ok = await reactivate(cp.id, {
      expectedRecordVersion: cp.recordVersion,
    });
    if (ok) await refresh();
  }

  async function onArchive(cp: FinanceCounterpartySummaryDto) {
    const reason = window.prompt('请输入归档原因（归档后不可恢复）：');
    if (!reason) return;
    const ok = await archive(cp.id, {
      expectedRecordVersion: cp.recordVersion,
      reason,
    });
    if (ok) await refresh();
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">对方单位与收款账户主数据</h2>
          <p className="text-xs text-gray-500">
            列表与详情默认脱敏；查看明文需 reveal 权限并写入审计。
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="rounded border border-gray-300 px-3 py-1 text-sm"
            onClick={() => void refresh()}
          >
            刷新
          </button>
          {canWrite && (
            <span
              className="rounded border border-dashed border-gray-300 px-3 py-1 text-xs text-gray-500"
              title="本 PR 提供 API；表单新建走 #369 后续 UI 增量"
            >
              新建对方单位（API 已就绪）
            </span>
          )}
        </div>
      </div>
      {loading && <div className="text-sm text-gray-500">加载中…</div>}
      {(error || actionError || payeeCreateError) && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {(error ?? actionError ?? payeeCreateError)?.message}
        </div>
      )}
      {data && data.rows.length === 0 && (
        <div className="rounded border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
          暂无对方单位，先通过 API 创建（待 #369 后续 UI 增量提供表单）。
        </div>
      )}
      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-2 py-2 text-left">名称</th>
                <th className="px-2 py-2 text-left">类型</th>
                <th className="px-2 py-2 text-left">税号（脱敏）</th>
                <th className="px-2 py-2 text-left">联系人</th>
                <th className="px-2 py-2 text-left">联系电话（脱敏）</th>
                <th className="px-2 py-2 text-left">状态</th>
                <th className="px-2 py-2 text-left">操作</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((cp) => (
                <React.Fragment key={cp.id}>
                  <tr className="border-t border-gray-100">
                    <td className="px-2 py-2 font-medium">
                      <button
                        className="text-blue-700 hover:underline"
                        onClick={() =>
                          setExpandedId(expandedId === cp.id ? null : cp.id)
                        }
                      >
                        {cp.name}
                      </button>
                      {cp.shortName && (
                        <span className="ml-1 text-xs text-gray-500">
                          ({cp.shortName})
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {COUNTERPARTY_TYPE_LABELS[cp.counterpartyType] ??
                        cp.counterpartyType}
                    </td>
                    <td
                      className="px-2 py-2 font-mono text-xs"
                      data-testid={`counterparty-tax-mask-${cp.id}`}
                    >
                      {cp.taxIdMask ?? '—'}
                    </td>
                    <td className="px-2 py-2">{cp.contactName ?? '—'}</td>
                    <td
                      className="px-2 py-2 font-mono text-xs"
                      data-testid={`counterparty-phone-mask-${cp.id}`}
                    >
                      {cp.contactPhoneMask ?? '—'}
                    </td>
                    <td className="px-2 py-2">
                      <StatusBadge status={cp.status} />
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        {canReveal && cp.taxIdMask && (
                          <button
                            className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"
                            onClick={() =>
                              openReveal(cp, 'tax_id', cp.taxIdMask)
                            }
                            data-testid={`reveal-tax-${cp.id}`}
                          >
                            查看税号
                          </button>
                        )}
                        {canReveal && cp.contactPhoneMask && (
                          <button
                            className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"
                            onClick={() =>
                              openReveal(cp, 'contact_phone', cp.contactPhoneMask)
                            }
                          >
                            查看电话
                          </button>
                        )}
                        {canManage && cp.status === 'active' && (
                          <button
                            className="rounded border border-yellow-200 bg-yellow-50 px-2 py-0.5 text-xs text-yellow-700"
                            onClick={() => void onSuspend(cp)}
                          >
                            暂停
                          </button>
                        )}
                        {canManage && cp.status === 'suspended' && (
                          <button
                            className="rounded border border-green-200 bg-green-50 px-2 py-0.5 text-xs text-green-700"
                            onClick={() => void onReactivate(cp)}
                          >
                            恢复
                          </button>
                        )}
                        {canManage && cp.status !== 'archived' && (
                          <button
                            className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-600"
                            onClick={() => void onArchive(cp)}
                          >
                            归档
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === cp.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={7} className="px-3 py-2">
                        <PayeeAccountsTable
                          counterparty={cp}
                          granted={granted}
                        />
                        {!canWritePayee && (
                          <div className="mt-2 text-xs text-gray-500">
                            没有 finance.payee_account:write 权限，无法新建账户。
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {revealState && (
        <RevealDialog
          state={revealState}
          onClose={() => {
            setRevealState(null);
            setRevealResult(null);
          }}
          onSubmit={(r, c) => void submitReveal(r, c)}
          result={revealResult}
          loading={actionLoading}
          error={actionError}
        />
      )}
    </div>
  );
}
